"use client";

import {
  type AsyncClickHandler,
  useAsyncClickHandler,
} from "@faire/react/hooks/useAsyncClickHandler";
import { SVGIconProps } from "@faire/slate-icons/types";
import { componentCls } from "@faire/slate/lib/tracking";
import { tsv } from "@faire/slate/lib/tsv";
import { ButtonBase } from "@faire/slate/private/button/ButtonBase";
import { ButtonIcon } from "@faire/slate/private/button/ButtonIcon";
import type {
  IconButtonSize,
  IconButtonVariant,
  IButtonWebProps,
  IconButtonShape,
} from "@faire/slate/private/button/types";
import { usePolimorphicAttributes } from "@faire/slate/private/button/usePolimorphicAttributes";
import React from "react";

type IProps = {
  /**
   * Button variant
   *
   * @default "primary"
   */
  variant?: IconButtonVariant;
  /**
   * Button shape
   *
   * @default "circle"
   */
  shape?: IconButtonShape;
  /**
   * Button size
   *
   * xxSmall (size 24px), xSmall (size 32px), small (size 40px), medium (size 48px)
   *
   * @default "medium"
   */
  size?: IconButtonSize;
  /**
   * Shows a loading spinner.
   *
   * @default false
   */
  loading?: boolean;
  /**
   * Whether the button is disabled.
   *
   * @default false
   */
  disabled?: boolean;
  /**
   * Whether the button has the destructive variant.
   *
   * @default false
   */
  destructive?: boolean;
  /**
   * The icon component to render
   */
  Icon: React.FC<SVGIconProps>;

  // DO NOT ADD any new prop without aligning with Slate team first
} & IButtonWebProps;

/**
 * Help users take action or make a choice in smaller, refined spaces.
 *
 * See [migration guide](migration-guide.mdx) for more information.
 *
 * {@link https://www.notion.so/faire/Icon-Button-2242efb5c25a80a8b448f97a37af897a | Slate/IconButton}
 */
export const IconButton: React.FC<IProps> = ({
  "aria-label": ariaLabel,
  destructive = false,
  disabled,
  Icon,
  loading,
  onClick,
  shape = "circle",
  size = "medium",
  variant = "primary",
  ...props
}) => {
  const { handleClick, isPending, isPendingDelayed } = useAsyncClickHandler(
    disabled,
    loading,
    onClick as AsyncClickHandler | undefined
  );
  const { as, linkProps } = usePolimorphicAttributes(props);

  const iconButtonCls = iconButtonStyle.cls({
    variant,
    destructive,
    shape,
    size,
    className: componentCls(),
  });

  const { linkTo, ...rest } = props;

  return (
    <ButtonBase
      {...rest}
      {...linkProps}
      aria-disabled={isPending || disabled}
      aria-label={isPendingDelayed ? undefined : ariaLabel}
      as={as}
      className={iconButtonCls}
      disabled={disabled}
      loading={isPending ? isPendingDelayed : undefined}
      onClick={handleClick}
      data-component-name="Slate/IconButton"
    >
      <ButtonIcon Icon={Icon} titleAccess="" className="fs-icon-button__icon" />
    </ButtonBase>
  );
};

const iconButtonStyle = tsv({
  base: "fs-icon-button",
  variants: {
    variant: {
      primary: "fs-icon-button--primary",
      secondary: "fs-icon-button--secondary",
      tertiary: "fs-icon-button--tertiary",
      plain: "fs-icon-button--plain",
    },
    shape: {
      circle: "rounded-(--slate-radius-component-round)",
      square: null,
    },
    size: {
      xxSmall: "fs-icon-button--xx-small",
      xSmall: "fs-icon-button--x-small",
      small: "fs-icon-button--small",
      medium: "fs-icon-button--medium",
    },
    destructive: { true: "fs-icon-button--destructive" },
  },
});
